import { useMemo } from "react";
import { Farm, BasicFarm } from "../models/farms";
import { StateEnveloped } from "../redux/helpers";
import { makeHookDeprecationWarn } from "../helpers/warnings";

import {
  useFarmsData,
  useCreateFarm,
  useUpdateFarm,
  useDeleteFarm,
  useFarmsGlobalActivity,
  useFarmsGlobalStatus,
} from "../next/hooks";

export type Data = {
  farms: Farm[];
  farmsById: Record<string, Farm | undefined>;
};
export type FarmsHookResult = StateEnveloped<Data> & {
  modifiers: {
    addFarm: (farmName: string) => Promise<BasicFarm>;
    removeFarm: (farmId: string) => Promise<void>;
    renameFarm: (farm: BasicFarm) => Promise<BasicFarm>;
  };
};
export type FarmsHook = () => FarmsHookResult;

const deprecationWarn = makeHookDeprecationWarn("useFarms");

/**
 * @deprecated Use next.hooks.useFarms instead.
 */
export const Hook: FarmsHook = () => {
  deprecationWarn();

  const data = useFarmsData();

  const status = useFarmsGlobalStatus();
  const activity = useFarmsGlobalActivity();

  const { mutate: createFarm } = useCreateFarm();
  const { mutate: updateFarm } = useUpdateFarm();
  const { mutate: deleteFarm } = useDeleteFarm();

  const modifiers = useMemo(
    () => ({
      addFarm: (farmName: string): Promise<BasicFarm> => {
        return createFarm({ name: farmName });
      },
      renameFarm: (farm: BasicFarm): Promise<BasicFarm> => {
        return updateFarm(farm);
      },
      removeFarm: async (farmId: string): Promise<void> => {
        await deleteFarm(farmId);
      },
    }),
    [createFarm, updateFarm, deleteFarm],
  );

  const memoizedResult = useMemo(
    () => ({
      status,
      activity,
      data,
      modifiers,
    }),
    [status, activity, data, modifiers],
  );

  return memoizedResult;
};

export default Hook;
